import { TechnologyContributorSelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Avatar } from '@components/shared/avatar.component';
import { CountryFlag } from '@components/shared/country-flag.component';
import { Tile } from '@components/shared/tile.component';
import { cn } from '@lib/utils';
import { useGetTechnologyContributors } from '@queries/technology-contributors.query';
import { useGetTechnology } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { formatDateUtc } from '@utils/date.util';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { Calendar, EditIcon } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { TechnologyContributorModal } from './technology-contributor-modal.component';
import { TechnologyContributorStatusBadge } from './technology-contributor-status-badge.component';

type ContributorItemProps = {
  canEdit: boolean;
  contributor: TechnologyContributorSelectDto;
  onEdit: () => void;
};

const ContributorItem: React.FC<ContributorItemProps> = (props) => {
  const { canEdit, contributor, onEdit } = props;

  const fullName = contributor.user.profile?.fullName || contributor.user.email;
  const title = contributor.user.profile?.title;

  return (
    <div className="group flex flex-col gap-2 bg-gray-100 h-fit rounded-md p-2">
      <div className="relative rounded-md bg-white p-2">
        <div className="absolute left-2 top-2">
          <Avatar
            altText={fullName}
            imageUrl={contributor.user.avatarUrl}
            initialText={fullName}
            size="md"
          />
        </div>
        {canEdit && (
          <div className={cn('absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100')}>
            <IconButton
              ariaLabel="Edit contributor"
              icon={<EditIcon />}
              onClick={onEdit}
              variant="secondary"
            />
          </div>
        )}
        <div className="min-w-0 flex-1 pl-14 pr-10">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <p className="min-w-0 truncate text-sm font-medium text-gray-900">{fullName}</p>
              <CountryFlag
                className="shrink-0"
                country={contributor.user.profile?.country}
              />
            </div>
            {title && <p className="text-xs text-gray-500 truncate">{title}</p>}
            <div className="mt-2">
              <TechnologyContributorStatusBadge contributionsEndDate={contributor.contributionsEndDate} />
            </div>
            {(contributor.contributionsStartDate || contributor.contributionsEndDate) && (
              <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
                <Calendar className="h-3 w-3 shrink-0" />
                <span>
                  {contributor.contributionsStartDate && contributor.contributionsEndDate
                    ? `${formatDateUtc(contributor.contributionsStartDate)} \u2013 ${formatDateUtc(contributor.contributionsEndDate)}`
                    : contributor.contributionsStartDate
                      ? `Started ${formatDateUtc(contributor.contributionsStartDate)}`
                      : null}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const TechnologyContributorsTile: React.FC = () => {
  const { technologyId } = useParams();
  const { data: technology } = useGetTechnology(technologyId);
  const { isCompanyAdmin, isInnovator, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contributorToEdit, setContributorToEdit] = useState<TechnologyContributorSelectDto | null>(null);

  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id,
  });
  const { data: contributors = [], isPending: isContributorsLoading } = useGetTechnologyContributors(technologyId);
  const canEdit = isInnovator && isCompanyAdmin && !isChildOrganizationView;

  const handleOpenModal = (contributor?: TechnologyContributorSelectDto) => {
    setContributorToEdit(contributor || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setContributorToEdit(null);
    setIsModalOpen(false);
  };

  const isEmpty = contributors.length === 0;

  const headerButton = canEdit ? { onClick: () => handleOpenModal(), text: '+ Add' } : undefined;

  return (
    <>
      <Tile
        className="min-h-[142px]"
        emptyState={{
          message: canEdit ? 'Add Contributor' : undefined,
          onClick: canEdit ? () => handleOpenModal() : undefined,
          title: 'No Key Contributors',
        }}
        header={{ button: headerButton, title: 'Key Contributors' }}
        isEmpty={isEmpty}
        isLoading={isContributorsLoading}
      >
        <div className="flex flex-col gap-4">
          {contributors.map((contributor) => (
            <ContributorItem
              canEdit={canEdit}
              contributor={contributor}
              key={contributor.id}
              onEdit={() => handleOpenModal(contributor)}
            />
          ))}
        </div>
      </Tile>
      {technologyId && canEdit && (
        <TechnologyContributorModal
          contributorToEdit={contributorToEdit}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          technologyId={technologyId}
        />
      )}
    </>
  );
};
